/* eslint-disable @typescript-eslint/no-unused-vars */
import { faro } from '@grafana/faro-web-sdk';
import jwtDecode from 'jwt-decode';
import { marked } from 'marked';
import TurndownService from 'turndown';

import { fileTypes, isHTML, LOGGED_IN_USER_INFO, TEXT_VALIDATIONS } from '../constants';

import { getParsedValueFromLocalStorage } from '../services/localStorage';

/**
 * @function isLogin
 * check whether local storage has access and refresh token or not
 * @returns Boolean
 */
export const isLogin = () => {
  const userInfo = getParsedValueFromLocalStorage(LOGGED_IN_USER_INFO);
  return !!(userInfo && userInfo.accessToken && userInfo.refreshToken);
};

export const generateSuffix = (day) => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = day % 100;
  return suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
};

/**
 * @function decodeJwt
 * @param {String} jwtToken
 * convert the jwt token
 * @returns Object
 */
export const decodeJwt = (jwtToken) => {
  try {
    const decodedInformation = jwtDecode(jwtToken);
    return decodedInformation;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return null;
  }
};

/**
 * @function epochToTimeSince
 * @param {Number} milliseconds
 * @returns time  string in required format
 */
export const epochToTimeSince = (milliseconds) => {
  const date = new Date(Number(milliseconds));
  let hours = date.getHours();
  let minutes = `${date.getMinutes()}`;
  const amPm = hours >= 12 ? ' PM' : ' AM';
  hours %= 12;
  hours = hours || 12; // the hour '0' should be '12'
  minutes = Number(minutes) < 10 ? `0${minutes}` : `${minutes}`;
  const strTime = `${hours}:${minutes}${amPm}`;
  if (`${date.toLocaleDateString()}` !== `${new Date().toLocaleDateString()}`) {
    return `${date.toLocaleDateString()} ${strTime}`;
  }
  return `Today ${strTime}`;
};

/**
 * @function formattedDate
 * @param {Date} date
 * @returns date string in required format
 */

export const formattedDate = (date, isSuffix = false) => {
  const regex = /^0[1-9]$/;
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const currentDateTime = new Date(date);
  let day = `0${currentDateTime.getDate()}`.slice(-2);
  if (isSuffix && regex.test(day)) {
    const [_, valuex] = day;
    day = valuex;
  }
  const suffixValue = generateSuffix(day);
  const formattedDateValue = `${day}${isSuffix ? suffixValue : ''}-${
    months[currentDateTime.getMonth()]
  }-${currentDateTime.getFullYear()}`;
  return formattedDateValue;
};

/**
 * @function debounce
 * @param {Function} func
 * @returns
 */

export const debounce = (func, timeout = 300) => {
  let timer;
  return (/** @type {any} */ ...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
};
/**
 * @function componentLazyLoader
 * @param {Function} lazyComponent
 * @param {Number} attemptsLeft
 * @returns Promise
 */

export const componentLazyLoader = (lazyComponent, attemptsLeft) => {
  return new Promise((resolve, reject) => {
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem('retry-lazy-refreshed') || 'false'
    );
    lazyComponent()
      .then((/** @type {any} */ component) => {
        window.sessionStorage.setItem('retry-lazy-refreshed', 'false');
        resolve(component);
      })
      .catch((/** @type {any} */ error) => {
        if (faro && faro.api && faro.api.pushError) {
          faro.api.pushError(error);
        }
        if (!hasRefreshed && attemptsLeft === 1) {
          // not been refreshed yet
          window.sessionStorage.setItem('retry-lazy-refreshed', 'true'); // we are now going to refresh
          return window.location.reload(); // refresh the page
        }
        // let us retry after 1500 ms
        setTimeout(() => {
          if (attemptsLeft === 1) {
            reject(error);
            return;
          }
          componentLazyLoader(lazyComponent, attemptsLeft - 1).then(resolve, reject);
        }, 1500);
        return null;
      });
  });
};

/**
 *
 * @param {String} content
 * @returns HTML content
 */
export const markdownToHTML = (content) => {
  // Ensure content is a string before passing to marked
  if (typeof content !== 'string') {
    // eslint-disable-next-line no-param-reassign
    content = '';
  }

  marked.setOptions({ baseUrl: 'https://' });
  return marked.parse(content);
};

/**
 *
 * @param {String} content HTML content
 * @returns Markdown of content
 */
export const htmlToMD = (content = '') => {
  const turndownService = new TurndownService({ headingStyle: 'atx', bulletListMarker: '-' });
  return turndownService.turndown(content);
};

/**
 * @function Nbsp
 * @returns non breaking space
 */

export const Nbsp = () => '\u00A0';

/**
 * @function IntlFormatedDate
 * @param {Date} date
 * @returns date string in required format
 */

export const IntlFormatedDate = (date) =>
  Intl.DateTimeFormat('en', { year: 'numeric', month: 'short', day: '2-digit' }).format(
    new Date(date)
  );

export const replaceDate = (utcTime, message) => {
  const localDate = epochToTimeSince(utcTime)?.split(' ')?.slice(1, 3)?.join(' ');
  const readable = formattedDate(utcTime, true).split('-').join(' ');
  const finalDateTime = `${localDate},${Nbsp()}${readable}`;
  const dateTimeRegex =
    /\b\d{1,2}(?::\d{2})?\s?[APap][Mm]?\s?(?:[A-Za-z]{2,4})?,?\s?\d{1,2}(?:st|nd|rd|th)?\s\w+\s\d{4}\b/;

  const updatedMessage = message.replace(dateTimeRegex, finalDateTime);
  return updatedMessage;
};

export const isValidOrganisationInputDomain = (input) => {
  // Define the regex for validating domains
  const domainRegex =
    /^(?:(?:(?:https?|ftp):)?\/\/)?(?:[^@\n]+@)?(?:www\.)?([-a-zA-Z0-9]+)(?:\.[a-z]{2,})+(?:[/?#]\S*)?$/i;

  // Check if input is a string
  if (typeof input === 'string') {
    // If input contains commas, split it into an array
    const domains = input.includes(',') ? input.split(',') : [input];

    // Trim any extra spaces and validate each domain
    return domains.every((domain) => domainRegex.test(domain.trim()));
  }

  // If input is not a string, return false (invalid)
  return false;
};
export const chunkArray = (data, numberOfBatches) => {
  const result = [];
  const batchSize = Math.ceil(data.length / numberOfBatches);

  for (let i = 0; i < data.length; i += batchSize) {
    result.push(data.slice(i, i + batchSize));
  }

  return result;
};

export function formatDateNew(dateString) {
  if (!dateString) return '';

  // Parse the date parts from yyyy-mm-dd
  const parts = dateString.split('-');
  if (parts.length !== 3) return dateString; // Return original if format doesn't match

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
  const day = parseInt(parts[2], 10);

  // Get month name
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // Get day suffix (st, nd, rd, th)
  // eslint-disable-next-line no-shadow
  function getDaySuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  // Create HTML with superscript suffix
  return `${day}<sup style="font-size: 0.6em;">${getDaySuffix(day)}</sup> ${
    monthNames[month]
  }, ${year}`;
}

export const validateCustomFields = (fieldData) => {
  const {
    value,
    fieldType,
    regexpValidation,
    numberValidation,
    paragraphValidation,
    uploadFileValidation,
    fileType,
    fileSize,
  } = fieldData;

  if (fieldType === 'TEXT') {
    if (
      regexpValidation?.regexpType === TEXT_VALIDATIONS[0] &&
      value !== '' &&
      !value?.includes(regexpValidation?.pattern)
    ) {
      return {
        isValid: false,
        errorMessage: `Input does not include ${regexpValidation?.pattern}`,
      };
    }

    if (
      regexpValidation?.regexpType === TEXT_VALIDATIONS[1] &&
      value !== '' &&
      value?.includes(regexpValidation?.pattern)
    ) {
      return {
        isValid: false,
        errorMessage: `Input should not include ${regexpValidation?.pattern}`,
      };
    }

    if (
      regexpValidation?.regexpType === TEXT_VALIDATIONS[2] &&
      value !== '' &&
      value !== regexpValidation?.pattern
    ) {
      return {
        isValid: false,
        errorMessage: `Input does not match the text: ${regexpValidation?.pattern}`,
      };
    }

    if (
      regexpValidation?.regexpType === TEXT_VALIDATIONS[3] &&
      value !== '' &&
      value === regexpValidation?.pattern
    ) {
      return {
        isValid: false,
        errorMessage: `Input should not match the text: ${regexpValidation?.pattern}`,
      };
    }

    if (
      regexpValidation?.regexpType === TEXT_VALIDATIONS[4] &&
      value !== '' &&
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
    ) {
      return { isValid: false, errorMessage: 'Email format is not valid' };
    }

    if (
      regexpValidation?.regexpType === TEXT_VALIDATIONS[5] &&
      value !== '' &&
      !/^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:\d{1,5})?(\/[^\s]*)?$/i.test(value)
    ) {
      return { isValid: false, errorMessage: 'URL is invalid' };
    }
  }

  if (
    fieldType === 'NUMBER' &&
    value !== '' &&
    (value < numberValidation?.min || value > numberValidation?.max)
  ) {
    return {
      isValid: false,
      errorMessage: `Number should be between ${numberValidation?.min} and ${numberValidation?.max}`,
    };
  }

  if (fieldType === 'PARAGRAPH') {
    if (paragraphValidation?.validationType === 'LENGTH') {
      if (
        paragraphValidation?.validationSubType === 'MINIMUM CHARACTERS' &&
        value?.length < paragraphValidation?.charCount
      ) {
        return {
          isValid: false,
          errorMessage: `Text should be atleast ${paragraphValidation?.charCount} characters long`,
        };
      }

      if (
        paragraphValidation?.validationSubType === 'MAXIMUM CHARACTERS' &&
        value?.length > paragraphValidation?.charCount
      ) {
        return {
          isValid: false,
          errorMessage: `Text should not be greater than ${paragraphValidation?.charCount} characters`,
        };
      }
    }
  }

  if (fieldType === 'UPLOAD FILE') {
    if (
      uploadFileValidation?.allowSpecificType &&
      !uploadFileValidation?.allowedFileTypes?.includes(fileType)
    ) {
      const allowedFileTypes = uploadFileValidation?.allowedFileTypes?.map((type) =>
        Object.keys(fileTypes).find((key) => fileTypes[key] === type)
      );

      return {
        isValid: false,
        errorMessage: `File type not allowed. Allowed file types are: ${allowedFileTypes?.join(
          ', '
        )}`,
      };
    }

    if (fileSize > uploadFileValidation?.maxFileSize) {
      return {
        isValid: false,
        errorMessage: `File size should not be more than ${(
          uploadFileValidation.maxFileSize /
          1024 /
          1024
        ).toFixed(2)}MB`,
      };
    }
  }

  return { isValid: true, errorMessage: '' };
};

export const isAnyRequiredCustomFieldEmpty = (
  customFormData,
  customFieldsValuesData,
  agency = undefined // passed from refferal form (for agency we want some fields mandatory) for other roles we want some other feidls mandatory
) => {
  let key = 'required';
  if (agency !== undefined) {
    key = agency ? 'isAgencyMandatory' : 'isReferralMandatory';
  }
  const requiredFields = customFormData?.filter((f) => f?.[key]) ?? [];

  return requiredFields.some((field) => {
    const value = customFieldsValuesData?.find(
      (item) => item?.fieldName === field?.fieldName
    )?.fieldValue;
    return (
      value === undefined ||
      value === null ||
      value === '' ||
      value === '<p><br></p>' || // ? this check is for the react-quill editor as it sometimes returns the text enclosed within paragraph tags
      value === '<br>' // ? this is also for react quill editor
    );
  });
};

export function isGoogleStorageURL(url) {
  try {
    const parsedURL = new URL(url);
    return parsedURL.hostname === 'storage.googleapis.com';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return false; // Not a valid URL
  }
}

export function isURL(url) {
  try {
    // eslint-disable-next-line prettier/prettier, no-new
    new URL(url);
    return true;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return false;
  }
}

export function millisecondToDate(millis) {
  const date = new Date(millis);

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() is zero-based
  const dd = String(date.getDate()).padStart(2, '0');

  const formatedDate = `${yyyy}-${mm}-${dd}`;
  return formatedDate;
}

export const isTokenValid = (token) => {
  try {
    // @ts-ignore
    const { exp } = decodeJwt(token);
    const currentTime = Math.floor(Date.now() / 1000);
    return exp && exp > currentTime;
  } catch {
    return false;
  }
};

export function hasAllPlaceholders(html, stringsArray) {
  if (!isHTML(html)) return false;
  const missingFeilds = [];
  stringsArray.forEach((s) => {
    // Ensure it starts with "."
    if (!s.startsWith('.')) return;

    // Check if HTML contains it inside {{ }}
    const pattern = new RegExp(`{{\\s*${s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*}}`);
    const value = pattern.test(html);
    if (!value) missingFeilds.push(s);
  });
  return missingFeilds;
}

export function getMissingFields(html, fieldsArray = []) {
  // Return empty array if html is invalid or not a string
  if (typeof html !== 'string' || !html.trim()) {
    return fieldsArray;
  }

  // Escape special characters in placeholders for regex
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // Check for missing fields
  const missingFields = fieldsArray.filter((field) => {
    const escapedField = escapeRegExp(field);
    const regex = new RegExp(escapedField, 'g');
    return !regex.test(html);
  });

  return missingFields;
}
